import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import StudyGateBadge from "../../common/StudyGateBadge/StudyGateBadge";
import {
  buildStudyGateOptions,
  gateNameForCode,
  normalizeGateCode,
} from "./isfStudyGateService";
import "./IsfStudyGatePicker.scss";

function GateOptionStatus({ isCurrent, isPast, isUpcoming }) {
  if (isCurrent) {
    return (
      <span className="isf-gate-picker__status-label is-current is-below">
        Current
      </span>
    );
  }
  if (isPast) {
    return (
      <span className="isf-gate-picker__status-label is-completed is-below">
        Completed
      </span>
    );
  }
  if (isUpcoming) {
    return (
      <span className="isf-gate-picker__status-label is-upcoming is-below">
        Upcoming
      </span>
    );
  }
  return null;
}

function GateOptionRow({
  gate,
  stepNumber,
  isViewing,
  isCurrent,
  isPast,
  isUpcoming,
  isNextPhase,
  isSkipAhead,
  onSelect,
}) {
  const disabled = isSkipAhead;
  const statusHint = isSkipAhead
    ? "Phases advance one step at a time"
    : isNextPhase
      ? "Advance study phase"
      : isPast
        ? "View completed phase (read-only)"
        : isUpcoming
          ? "Upcoming"
          : null;

  return (
    <button
      type="button"
      role="option"
      aria-selected={isViewing}
      aria-disabled={disabled}
      aria-label={gate.name}
      title={statusHint || gate.name}
      className={`isf-gate-picker__option${
        isViewing ? " is-viewing" : ""
      }${isCurrent ? " is-current" : ""}${isPast ? " is-past" : ""}${
        isUpcoming ? " is-upcoming" : ""
      }${isNextPhase ? " is-next-phase" : ""}${
        disabled ? " is-disabled" : ""
      }`}
      disabled={disabled}
      onClick={() => !disabled && onSelect?.(gate.code)}
    >
      <span className="isf-gate-picker__step" aria-hidden="true">
        {stepNumber}
      </span>
      <div className="isf-gate-picker__option-stack">
        <StudyGateBadge code={gate.code} label={gate.name} compact />
        <GateOptionStatus
          isCurrent={isCurrent}
          isPast={isPast}
          isUpcoming={isUpcoming && !isNextPhase}
        />
        {isNextPhase ? (
          <span className="isf-gate-picker__status-label is-next-phase is-below">
            Next phase
          </span>
        ) : null}
        {isSkipAhead ? (
          <span className="isf-gate-picker__status-label is-blocked is-below">
            One step at a time
          </span>
        ) : null}
      </div>
      {isViewing ? (
        <span className="isf-gate-picker__check" aria-hidden="true">
          <i className="fas fa-check" />
        </span>
      ) : null}
    </button>
  );
}

const IsfStudyGatePicker = ({
  gates = [],
  value,
  actualGateCode,
  onChange,
  onAdvanceRequest,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const options = useMemo(() => buildStudyGateOptions(gates), [gates]);
  const selectedCode = normalizeGateCode(value || actualGateCode || "PRE_STUDY");
  const actualCode = normalizeGateCode(actualGateCode || selectedCode);
  const actualSortOrder =
    options.find((gate) => gate.code === actualCode)?.sortOrder ?? 0;
  const selectedGate =
    options.find((gate) => gate.code === selectedCode) ||
    options[0] || {
      code: selectedCode,
      name: gateNameForCode(selectedCode),
    };
  const isViewingOtherGate = selectedCode !== actualCode;
  const selectedSortOrder = selectedGate.sortOrder ?? 0;

  const triggerStatusLabel = !isViewingOtherGate
    ? "Current"
    : selectedSortOrder > actualSortOrder
      ? "Upcoming"
      : selectedSortOrder < actualSortOrder
        ? "Completed"
        : "Read-only";

  const triggerStatusClass = !isViewingOtherGate
    ? "is-current"
    : selectedSortOrder > actualSortOrder
      ? "is-upcoming"
      : selectedSortOrder < actualSortOrder
        ? "is-completed"
        : "is-preview";

  const updateMenuPosition = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuStyle({
      top: rect.bottom + 6,
      left: rect.left,
      width: "max-content",
      minWidth: rect.width,
      maxWidth: "min(240px, calc(100vw - 16px))",
    });
  };

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    const onResize = () => updateMenuPosition();
    const onPointerDown = (event) => {
      const target = event.target;
      if (
        triggerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const handleSelect = (code) => {
    setOpen(false);
    const normalized = normalizeGateCode(code);
    if (!normalized || normalized === selectedCode) return;

    const targetSort =
      options.find((gate) => gate.code === normalized)?.sortOrder ?? 0;

    if (normalized === actualCode) {
      onChange?.(normalized);
      return;
    }

    if (targetSort < actualSortOrder) {
      onChange?.(normalized);
      return;
    }

    if (targetSort === actualSortOrder + 1) {
      onAdvanceRequest?.(normalized);
      return;
    }
  };

  const menu = open
    ? ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="isf-gate-picker__menu"
          style={menuStyle || undefined}
          role="listbox"
          aria-label="Study phase"
        >
          <div className="isf-gate-picker__menu-title">Study phases</div>
          <p className="isf-gate-picker__menu-hint">
            View completed phases read-only. Advance only to the next phase.
          </p>
          {options.map((gate, index) => (
            <GateOptionRow
              key={gate.code}
              gate={gate}
              stepNumber={index + 1}
              isViewing={gate.code === selectedCode}
              isCurrent={gate.code === actualCode}
              isPast={gate.sortOrder < actualSortOrder}
              isUpcoming={gate.sortOrder > actualSortOrder}
              isNextPhase={gate.sortOrder === actualSortOrder + 1}
              isSkipAhead={gate.sortOrder > actualSortOrder + 1}
              onSelect={handleSelect}
            />
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="isf-gate-picker" data-tour="isf-study-gate-picker">
      <span className="isf-gate-picker__label" id="isf-gate-picker-label">
        Phase
      </span>
      <button
        ref={triggerRef}
        type="button"
        className={`isf-gate-picker__trigger${
          isViewingOtherGate ? " is-preview" : " is-current-phase"
        }`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby="isf-gate-picker-label"
        title={
          isViewingOtherGate
            ? `Viewing ${selectedGate.name} (read-only). Current study phase: ${gateNameForCode(actualCode)}`
            : `Current study phase: ${selectedGate.name}. Select the next phase to advance.`
        }
      >
        <StudyGateBadge
          code={selectedGate.code}
          label={selectedGate.name}
          compact
        />
        <span
          className={`isf-gate-picker__status-label is-inline ${triggerStatusClass}`}
        >
          {triggerStatusLabel}
        </span>
        <i
          className={`fas fa-chevron-down isf-gate-picker__chevron${
            open ? " is-open" : ""
          }`}
          aria-hidden="true"
        />
      </button>
      {menu}
    </div>
  );
};

export default IsfStudyGatePicker;
